import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import ContractorPortalApi from "../../../../../../infrastructure/api/contractorPortalApi";
import { formatCurrency } from "../../../../../../infrastructure/utils/formatters";
import { CONTRACTOR_CONTRACT_PHASE } from "../../utils/contractorContractUx";
import { CONTRACT_UX } from "./contractUxTheme";

const openBlob = (blob, fileName = "guarantee-document") => {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const toEnDigits = (value) =>
  String(value ?? "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const onlyDigits = (value) => toEnDigits(value).replace(/\D/g, "");

const Row = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 96 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} data-testid={`guarantee-row-${label}`}>
      {value}
    </Typography>
  </Stack>
);

const emptyCheque = {
  sayadiId: "",
  chequeSerial: "",
  bankName: "",
  accountNumber: "",
  branchName: "",
  issuerName: "",
};

const isAllowedGuaranteeFile = (file) => {
  if (!file) return false;
  const allowed = /image\/(jpeg|jpg|png|webp)|application\/pdf/i.test(file.type);
  if (allowed) return true;
  const name = (file.name || "").toLowerCase();
  return /\.(jpe?g|png|webp|pdf)$/i.test(name);
};

/**
 * Phase 7.7.4 — Compact guarantee status + upload card with cheque metadata form.
 * Flow: fill cheque → pick file → Save (submit for manager review).
 */
const GuaranteeStatusCard = ({ contract, ux, onUploaded }) => {
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [cheque, setCheque] = useState(emptyCheque);
  const [pendingFile, setPendingFile] = useState(null);

  if (!contract?.contractId || !ux?.showGuarantee) return null;

  const hasPlatformCheque = Boolean(ux.guaranteeSayadiId);
  const canUpload = Boolean(ux.canUploadGuarantee);
  const hasDoc = Boolean(ux.hasGuaranteeDocument);
  const amount =
    ux.guaranteeAmountIrr != null
      ? formatCurrency(ux.guaranteeAmountIrr, "rial")
      : "—";

  const guaranteeAlert = (() => {
    const statusLabel = ux.guaranteeStatusLabel || "";
    if (
      ux.phase === CONTRACTOR_CONTRACT_PHASE.ACTIVE ||
      statusLabel === "تأیید شده"
    ) {
      return {
        severity: "success",
        text: "ضمانت تأیید شده است و دسترسی پروژه‌ها فعال می‌باشد.",
      };
    }
    if (statusLabel === "در حال بررسی" || statusLabel === "دریافت شده") {
      return {
        severity: "info",
        text: "مدرک دریافت شده و در حال بررسی توسط مدیر است. پس از تأیید نهایی و امضا، دسترسی پروژه‌ها فعال می‌شود.",
      };
    }
    if (statusLabel === "نیاز به ارسال مجدد") {
      return {
        severity: "error",
        text: "ضمانت رد شده است. لطفاً اطلاعات و تصویر جدید چک صیادی را وارد کرده و ذخیره کنید.",
      };
    }
    if (hasPlatformCheque) {
      if (!canUpload) {
        return {
          severity: "warning",
          text: "پس از امضا امکان تغییر تصویر چک وجود ندارد. چک فیزیکی را مطابق مشخصات به دفتر سولارنت پست کنید.",
        };
      }
      return {
        severity: "warning",
        text: "شعبه و نام صادرکننده را وارد کنید، تصویر چک را بارگذاری کنید و سپس قرارداد را امضا نمایید.",
      };
    }
    if (!canUpload) {
      return {
        severity: "info",
        text: "پس از امضای پیمانکار، انتخاب یا تغییر تصویر چک غیرفعال است.",
      };
    }
    return {
      severity: "warning",
      text: "اطلاعات چک و تصویر را وارد کنید، سپس با دکمه ذخیره برای بررسی مدیر و صدور نهایی ارسال کنید.",
    };
  })();

  const setChequeField = (key, value) => {
    setCheque((prev) => ({ ...prev, [key]: value }));
  };

  const validateCheque = () => {
    const branchName = String(cheque.branchName || "").trim();
    const issuerName = String(cheque.issuerName || "").trim();

    if (!branchName) {
      toast.error("نام شعبه الزامی است.");
      return null;
    }
    if (!issuerName) {
      toast.error("نام صادرکننده الزامی است.");
      return null;
    }

    if (hasPlatformCheque) {
      return {
        sayadiId: onlyDigits(ux.guaranteeSayadiId),
        chequeSerial: toEnDigits(ux.guaranteeChequeSerial || "").trim(),
        bankName: String(ux.guaranteeBankName || "").trim(),
        accountNumber: onlyDigits(ux.guaranteeAccountNumber),
        branchName,
        issuerName,
      };
    }

    const sayadiId = onlyDigits(cheque.sayadiId);
    const accountNumber = onlyDigits(cheque.accountNumber);
    const chequeSerial = toEnDigits(cheque.chequeSerial).trim();
    const bankName = String(cheque.bankName || "").trim();

    if (!sayadiId) {
      toast.error("شناسه صیادی الزامی است.");
      return null;
    }
    if (sayadiId.length !== 16) {
      toast.error("شناسه صیادی باید ۱۶ رقم باشد.");
      return null;
    }
    if (!chequeSerial) {
      toast.error("سریال چک الزامی است.");
      return null;
    }
    if (!bankName) {
      toast.error("نام بانک الزامی است.");
      return null;
    }
    if (!accountNumber) {
      toast.error("شماره حساب الزامی است.");
      return null;
    }
    if (accountNumber.length < 6 || accountNumber.length > 20) {
      toast.error("شماره حساب باید بین ۶ تا ۲۰ رقم باشد.");
      return null;
    }

    return { sayadiId, chequeSerial, bankName, accountNumber, branchName, issuerName };
  };

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!isAllowedGuaranteeFile(file)) {
      toast.error("فقط تصویر (JPG/PNG/WEBP) یا PDF مجاز است.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("حجم فایل نباید بیشتر از ۱۰ مگابایت باشد.");
      return;
    }

    setPendingFile(file);
  };

  const handleSave = async () => {
    const chequeInfo = validateCheque();
    if (!chequeInfo) return;

    if (!pendingFile) {
      toast.error("لطفاً ابتدا تصویر یا PDF چک را انتخاب کنید.");
      return;
    }

    setSaving(true);
    const res = await ContractorPortalApi.uploadGuaranteeDocument(
      contract.contractId,
      pendingFile,
      chequeInfo
    );
    setSaving(false);
    if (!res?.isSuccess) {
      toast.error(res?.message || "ذخیره ناموفق بود.");
      return;
    }
    toast.success(
      res.message ||
        "اطلاعات چک ذخیره شد و برای بررسی مدیر و صدور نهایی ارسال گردید."
    );
    setPendingFile(null);
    setCheque(emptyCheque);
    if (typeof onUploaded === "function") onUploaded(res.data);
  };

  const handleDownload = async () => {
    setDownloading(true);
    const res = await ContractorPortalApi.downloadGuaranteeDocument(
      contract.contractId
    );
    setDownloading(false);
    if (!res?.isSuccess || !res.data) {
      toast.error(res?.message || "دانلود ناموفق بود.");
      return;
    }
    openBlob(res.data, "cheque-guarantee");
  };

  return (
    <Box
      data-testid="guarantee-status-card"
      id="guarantee-upload-section"
      dir="rtl"
      sx={{
        height: "100%",
        borderRadius: 2,
        bgcolor: "#fff",
        border: `1px solid ${CONTRACT_UX.cardBorder}`,
        px: CONTRACT_UX.cardPadding,
        py: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
        تضمین حسن انجام کار
      </Typography>

      <Stack spacing={1.1}>
        <Row label="مبلغ ضمانت" value={amount} />
        <Row label="نوع ضمانت" value={ux.guaranteeTypeLabel || "چک صیادی"} />
        <Typography
          component="span"
          variant="body2"
          fontWeight={700}
          data-testid="guarantee-status-label"
          sx={{ display: "none" }}
        >
          {ux.guaranteeStatusLabel}
        </Typography>
        <Row label="وضعیت ارسال" value={ux.guaranteeStatusLabel} />
        <Row
          label="مدرک مورد نیاز"
          value={ux.requiredDocumentLabel || "چک صیادی فیزیکی"}
        />
        {ux.guaranteeSayadiId ? (
          <>
            <Row label="شناسه صیادی" value={ux.guaranteeSayadiId} />
            <Row label="سریال" value={ux.guaranteeChequeSerial || "—"} />
            <Row label="بانک" value={ux.guaranteeBankName || "—"} />
            <Row label="شماره حساب" value={ux.guaranteeAccountNumber || "—"} />
            {ux.guaranteeBranchName ? (
              <Row label="شعبه" value={ux.guaranteeBranchName} />
            ) : null}
            {ux.guaranteeIssuerName ? (
              <Row label="صادرکننده" value={ux.guaranteeIssuerName} />
            ) : null}
            {ux.guaranteeChequeDateFa ? (
              <Row label="تاریخ چک" value={ux.guaranteeChequeDateFa} />
            ) : null}
          </>
        ) : null}
      </Stack>

      {ux.solarentOfficeMailingAddress &&
      ux.phase === CONTRACTOR_CONTRACT_PHASE.AWAITING_GUARANTEE ? (
        <Alert severity="warning" sx={{ mt: 1.75 }} data-testid="guarantee-mail-instructions">
          <Typography variant="body2" fontWeight={700} gutterBottom>
            دستورالعمل ارسال پستی چک
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {ux.postSignMessage ||
              "چک را دقیقاً مطابق مشخصات بالا تکمیل کنید و به آدرس زیر پست نمایید."}
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
            آدرس دفتر:
          </Typography>
          <Typography variant="body2">{ux.solarentOfficeMailingAddress}</Typography>
        </Alert>
      ) : (
        <Alert
          severity={guaranteeAlert.severity}
          data-testid="guarantee-access-alert"
          sx={{
            mt: 1.75,
            py: 0.5,
            ...(guaranteeAlert.severity === "warning"
              ? {
                  bgcolor: CONTRACT_UX.warningSoft,
                  color: "text.primary",
                  "& .MuiAlert-icon": { color: CONTRACT_UX.warning },
                }
              : {}),
          }}
        >
          {guaranteeAlert.text}
        </Alert>
      )}

      {canUpload ? (
        <Box data-testid="guarantee-cheque-form" sx={{ mt: 1.75 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
            {hasPlatformCheque
              ? "تکمیل اطلاعات توسط پیمانکار"
              : "اطلاعات چک صیادی"}
          </Typography>
          <Grid container spacing={1.25}>
            {!hasPlatformCheque ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="شناسه صیادی"
                    value={cheque.sayadiId}
                    onChange={(e) =>
                      setChequeField("sayadiId", onlyDigits(e.target.value).slice(0, 16))
                    }
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 16,
                      "data-testid": "guarantee-sayadi-id",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="سریال چک"
                    value={cheque.chequeSerial}
                    onChange={(e) =>
                      setChequeField(
                        "chequeSerial",
                        toEnDigits(e.target.value).slice(0, 64)
                      )
                    }
                    inputProps={{
                      maxLength: 64,
                      "data-testid": "guarantee-cheque-serial",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="بانک"
                    value={cheque.bankName}
                    onChange={(e) =>
                      setChequeField("bankName", e.target.value.slice(0, 120))
                    }
                    inputProps={{
                      maxLength: 120,
                      "data-testid": "guarantee-bank-name",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="شماره حساب"
                    value={cheque.accountNumber}
                    onChange={(e) =>
                      setChequeField(
                        "accountNumber",
                        onlyDigits(e.target.value).slice(0, 20)
                      )
                    }
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 20,
                      "data-testid": "guarantee-account-number",
                    }}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="شعبه *"
                value={cheque.branchName}
                onChange={(e) =>
                  setChequeField("branchName", e.target.value.slice(0, 120))
                }
                inputProps={{
                  maxLength: 120,
                  "data-testid": "guarantee-branch-name",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="نام صادرکننده *"
                value={cheque.issuerName}
                onChange={(e) =>
                  setChequeField("issuerName", e.target.value.slice(0, 200))
                }
                inputProps={{
                  maxLength: 200,
                  "data-testid": "guarantee-issuer-name",
                }}
              />
            </Grid>
          </Grid>

          {pendingFile ? (
            <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }} data-testid="guarantee-file-selected">
              فایل انتخاب‌شده: {pendingFile.name}
              {" — "}هنوز ذخیره نشده؛ برای ارسال به بررسی مدیر دکمه ذخیره را بزنید.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: "block" }}>
              تصویر چک را قبل از امضا انتخاب و ذخیره کنید؛ پس از امضا امکان تغییر تصویر وجود ندارد.
            </Typography>
          )}
        </Box>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.75 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.pdf,application/pdf"
          hidden
          onChange={handleFilePick}
        />
        {canUpload ? (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              disabled={saving}
              onClick={() => inputRef.current?.click()}
              data-testid="guarantee-upload-btn"
              sx={{
                borderColor: CONTRACT_UX.primary,
                color: CONTRACT_UX.primary,
                fontWeight: 700,
              }}
            >
              {pendingFile
                ? "تغییر فایل"
                : hasDoc
                ? "انتخاب تصویر جایگزین"
                : "انتخاب تصویر چک"}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={
                saving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              disabled={saving || !pendingFile}
              onClick={handleSave}
              data-testid="guarantee-save-btn"
              sx={{
                bgcolor: CONTRACT_UX.primary,
                fontWeight: 700,
                "&:hover": { bgcolor: CONTRACT_UX.primaryDark },
              }}
            >
              ذخیره و ارسال برای بررسی
            </Button>
          </>
        ) : null}
        {(hasDoc || ux.canViewGuarantee) && (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloading ? <CircularProgress size={14} /> : <DownloadIcon />
            }
            disabled={downloading || !hasDoc}
            onClick={handleDownload}
            data-testid="guarantee-download-btn"
            sx={{ borderColor: CONTRACT_UX.primary, color: CONTRACT_UX.primary }}
          >
            مشاهده مدرک
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default GuaranteeStatusCard;
